/**
 * Idle Session Monitor Composable
 * 
 * Monitors user activity and checks session validity when idle.
 * Automatically logs out users when their session expires.
 * 
 * Features:
 * - Tracks user activity (mouse, keyboard, touch, scroll)
 * - Checks session validity after period of inactivity
 * - Proactively checks before session lifetime expires
 * - Integrates with existing 401 interceptor in api.ts
 */

import { onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { authService } from '@/services/api/auth.service'

export interface IdleSessionMonitorOptions {
  /**
   * Minutes of inactivity before checking session validity
   * @default 15
   */
  idleTimeoutMinutes?: number
  
  /**
   * Total session lifetime in minutes (should match Laravel SESSION_LIFETIME)
   * @default 120
   */
  sessionLifetimeMinutes?: number
  
  /**
   * How often to perform periodic session checks (in minutes)
   * @default 10
   */
  periodicCheckIntervalMinutes?: number
  
  /**
   * Show console logs for debugging
   * @default false
   */
  debug?: boolean
}

export function useIdleSessionMonitor(options: IdleSessionMonitorOptions = {}) {
  const {
    idleTimeoutMinutes = 15,
    sessionLifetimeMinutes = 120,
    periodicCheckIntervalMinutes = 10,
    debug = false,
  } = options

  const router = useRouter()
  let idleTimer: number | null = null
  let sessionCheckTimer: number | null = null
  const lastActivity = ref(Date.now())

  const log = (...args: any[]) => {
    if (debug) {
      console.log('[SessionMonitor]', ...args)
    }
  }

  /**
   * Reset the idle timer whenever user activity is detected
   */
  const resetIdleTimer = () => {
    lastActivity.value = Date.now()
    
    if (idleTimer) clearTimeout(idleTimer)
    
    // After X minutes of inactivity, check if session is still valid
    idleTimer = window.setTimeout(async () => {
      log(`${idleTimeoutMinutes} minutes of inactivity detected, checking session...`)
      await checkSessionValidity()
    }, idleTimeoutMinutes * 60 * 1000)
  }

  /**
   * Check if the session is still valid
   */
  const checkSessionValidity = async () => {
    try {
      const isAuth = await authService.isAuthenticated()
      if (!isAuth) {
        log('Session invalid, logging out...')
        handleSessionExpired()
      } else {
        log('Session still valid')
      }
    } catch (error) {
      // If the check fails, assume session expired
      log('Session check failed, logging out...', error)
      handleSessionExpired()
    }
  }

  /**
   * Handle session expiration by cleaning up and redirecting to login
   */
  const handleSessionExpired = () => {
    log('Handling session expiration')
    
    // Clear local storage
    localStorage.removeItem('user')
    localStorage.removeItem('workspace')
    
    // Redirect to login
    router.push('/login')
    
    // Optional: You can add a toast notification here
    // Example: useToast().add({ type: 'warning', message: 'Your session has expired. Please log in again.' })
  }

  /**
   * Periodically check session before it expires
   * This catches cases where the user is idle but the session is about to expire
   */
  const checkSessionBeforeExpiry = async () => {
    const timeSinceActivity = Date.now() - lastActivity.value
    const minutesSinceActivity = timeSinceActivity / 1000 / 60
    
    log(`Periodic check: ${Math.floor(minutesSinceActivity)} minutes since last activity`)
    
    // Check if we're approaching session expiry (5 minutes before)
    if (minutesSinceActivity >= sessionLifetimeMinutes - 5) {
      log('Approaching session expiry, checking validity...')
      await checkSessionValidity()
    }
  }

  /**
   * Set up event listeners for user activity
   */
  const setupActivityListeners = () => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
    
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer, { passive: true })
    })
    
    log('Activity listeners set up')
  }

  /**
   * Clean up event listeners
   */
  const cleanupActivityListeners = () => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
    
    events.forEach(event => {
      document.removeEventListener(event, resetIdleTimer)
    })
    
    log('Activity listeners cleaned up')
  }

  /**
   * Initialize the session monitor
   */
  onMounted(() => {
    log('Session monitor initialized', {
      idleTimeoutMinutes,
      sessionLifetimeMinutes,
      periodicCheckIntervalMinutes,
    })
    
    setupActivityListeners()
    resetIdleTimer()
    
    // Periodically check session status
    sessionCheckTimer = window.setInterval(
      checkSessionBeforeExpiry,
      periodicCheckIntervalMinutes * 60 * 1000
    )
  })

  /**
   * Clean up when component unmounts
   */
  onUnmounted(() => {
    log('Session monitor cleaned up')
    
    cleanupActivityListeners()
    
    if (idleTimer) clearTimeout(idleTimer)
    if (sessionCheckTimer) clearInterval(sessionCheckTimer)
  })

  return {
    /**
     * Manually trigger a session check
     */
    checkSession: checkSessionValidity,
    
    /**
     * Get the last activity timestamp
     */
    getLastActivity: () => lastActivity.value,
  }
}
